// City banners and country names: DOM elements over the canvas, positioned every frame from the
// camera (crisp text at any zoom, no texture atlas).
//
//   snapshot.cities ─┐
//   city-state capitals with no city ─┴─► entries (capitals, then population)
//        ─► candidates by zoom band ─► project ─► screen-rect declutter, banners already on screen
//           keep their place first ─► at most MAX_BANNERS shown
//   snapshot.owner  ─► largest territory per country ─► its innermost hex = label anchor,
//                      the territory's long axis = the way the name runs
//                   ─► name spread over the axis (fitLabel) at the anchor, else at spots further
//                      along the axis, from the largest size down to the smallest legible one
//   snapshot.fronts ─► a tag "Front as of <date>" on every front older than a turn
//   HUD boxes (setObstacles) ─► kept clear by names, tags and banners alike
//
// Cost: a name's spots on the screen, and whether the ground under each is its country's (a march
// of GROUND_SAMPLES rays), are kept while the camera, the viewport and the snapshot stand still; only
// the declutter runs again. A moving camera re-checks each name on its own schedule (see groundOk).
// prepare() does the once-per-snapshot work while the labels are hidden.
//
//   ┌────┬───────────────────┬────────┬───────┐
//   │ ★  │ ROME              │ 2.5M   │ ⚔ ⚑   │   plate: owner colours, star = capital
//   └────┴───────────────────┴────────┴───────┘   pop with its year; marks: war, near the front, occupied
//
// Truth: every banner and label carries the source ids of its record (cities.json /
// countries.json); a mark is drawn only when a record backs it (cityStatus.ts). counts().unsourced
// reports any banner or name drawn without one (the harness gate).

import * as THREE from 'three/webgpu';
import { HEX_COUNT, WORLD_W, hexAtWorld, hexCenter, hexCol, hexRow, neighbour } from '../../core/grid.ts';
import type { City, SourceId } from '../../core/schema.ts';
import type { WorldSnapshot } from '../../world/api.ts';
import { wrapDelta } from '../camera.ts';
import { cityMarks, citySite, type Mark, type StatusInput } from './cityStatus.ts';
import { FRESH_DAYS, SCRUB_MS, frontRecords, type FrontRecord } from './fronts.ts';
import { hudRects, type ScreenRect } from './hudRects.ts';
import { fitLabel, inkOn, principalAxis, turnedHalf, type Axis } from './labelGeo.ts';

/** A country or other actor that can own hexes, by snapshot actor index. */
export interface ActorLabel {
  index: number;
  /** Actor id in the world API (ActorId, e.g. 'RUS'): links snapshot.fronts[].controller to an index. */
  id?: string;
  /** Country.city_state: draws a ring and banner on the capital hex even when it owns no hex. */
  cityState?: boolean;
  name: string;
  colors: { primary: string; secondary: string };
  sources: SourceId[];
  /** Hex of the capital: the label goes on the territory holding it (Denmark, not Greenland). */
  capitalHex?: number;
}

export interface LabelCounts {
  banners: number;
  countries: number;
  /** Status marks (war, near the front, occupied) on the banners drawn; each carries sources. */
  marks: number;
  /** Drawn markers that carry no source id (must stay 0). */
  unsourced: number;
}

// Which cities show at a camera height (hex radii): the first band whose limit exceeds it.
const CITY_BANDS: ReadonlyArray<{ below: number; minPop: number; capitals: boolean }> = [
  { below: 28, minPop: 0, capitals: true },
  { below: 70, minPop: 1_000_000, capitals: true },
  { below: 160, minPop: 2_000_000, capitals: true },
  { below: 300, minPop: 8_000_000, capitals: false },
];
const SMALL_FROM = CITY_BANDS[1].below; // banners lose population and shrink from this height up
const MAX_BANNERS = 150;
const COUNTRY_MIN_HEIGHT = 40;
const FRONT_TAG_BELOW = 200; // fronts get their date tag under this camera height
const BANNER_LIFT_PX = 10;
const LABEL_PAD_PX = 4;
const LABEL_Y = 0.25; // world units above the ground
const FADE_MS = 160;
const RIGHT_ANGLE = Math.PI / 2;
const HALF = 0.5;

// Banner layout in px; the CSS below carries only the look, these numbers the sizes.
type BannerSize = 'full' | 'small';
interface Metrics {
  height: number;
  font: number;
  plate: number;
  mark: number;
}
const METRICS: Record<BannerSize, Metrics> = {
  full: { height: 20, font: 11, plate: 18, mark: 12 },
  small: { height: 16, font: 9.5, plate: 13, mark: 10 },
};
const BANNER_FAMILY = '"Alegreya Sans SC", "Alegreya Sans", "Trebuchet MS", sans-serif';
const COUNTRY_FAMILY = '"Cinzel", "Trajan Pro", "Cormorant SC", Georgia, serif';
const TRACKING_EM = 0.04; // banner letter spacing
const BORDER_PX = 1;
const NAME_PAD_PX = 11;
const POP_PAD_PX = 10;
const YEAR_PX = 8;
const YEAR_GAP_PX = 2;
const TAG_PX = 8;
const TAG_GAP_PX = 4;
const CENTRE_TAG = 'urban area';
const CENTRE_NOTE = 'Urban centre of';
// A centre of fewer towns than this is one town: no tag.
const CENTRE_MIN_TOWNS = 2;
const MARKS_PAD_PX = 9;
const MARK_GAP_PX = 3;
const MEASURE_EM = 100; // country names are measured at this size, stored in em

// A country name is tried at these shares of its fitted size (never under the smallest legible size),
// and at these fractions of the territory's length from its anchor: along its axis, and to its side
// (a HUD band across the anchor row, like Russia under the top ribbon, is stepped over).
const SIZE_STEPS = [1, 0.85, 0.72, 0.6, 0.5];
const SPOT_OFFSETS = [0, 0.18, -0.18, 0.34, -0.34];
const SIDE_OFFSETS = [0, 0.06, -0.06, 0.12, -0.12];
const SPOT_SPAN_LOSS = 2; // a spot |f| off the anchor has 1 - SPOT_SPAN_LOSS * |f| of the axis to itself
const SPOT_SPAN_MIN = 0.25;
const SIZE_FLOOR_PX = 11;
const UNOWNED = 0;
// Ground pick: a pixel's ray is walked in steps of this many units from the tallest ground down to the sea,
// and stops at the first hex it dips under (the hex the eye sees there), in at most this many steps.
const GROUND_MARCH_STEP = 0.2;
const GROUND_MARCH_MAX = 120;
// A name is checked at this many points along its length, both ends included, and at least this share
// of them must lie over its own country (a coast or a gap may take the rest, never another country).
const GROUND_SAMPLES = 33;
const OWN_SHARE_MIN = 0.8;
const GROUND_MISSES_MAX = Math.floor(GROUND_SAMPLES * (1 - OWN_SHARE_MIN));

// The view is the camera's world matrix, its projection scale, the camera x and the viewport. It counts
// as moved when any of these numbers moves by more than VIEW_EPS (well under a pixel at any zoom).
const VIEW_EPS = 1e-4;
const VIEW_MATRIX = 16;
const VIEW_LEN = VIEW_MATRIX + 5;
// A ground verdict of an earlier view stands while the camera has travelled less than this since (a
// travel unit: one radian of turn, or a whole camera height of move). A name's own share, up to twice
// this, is what it stands for, so the names of a moving camera are checked one after another and not all
// on the same frame.
const TRAVEL_STANDS = 0.02;
const TRAVEL_SPREAD = 6;
const LEAP_TRAVEL = 1; // a viewport change or a jump: no earlier verdict stands
// Matrix elements of the camera's axes (column-major: x, y, z axes, then the position).
const ROTATION = [0, 1, 2, 4, 5, 6, 8, 9, 10];

/** How coarse a sample index is: the ends first, then the middle, then the quarters, and so on. */
function coarseness(i: number): number {
  return i === 0 ? GROUND_SAMPLES : i & -i;
}

// Coarse samples first: a name that overruns a neighbour is usually caught by the first few.
const GROUND_ORDER = Array.from({ length: GROUND_SAMPLES }, (_, i) => i).sort((a, b) => coarseness(b) - coarseness(a) || a - b);

// Population years from here on are modelled, not counted (GHS urban centre database, 2025 epoch).
const MODELLED_FROM = 2025;
const MODELLED_TAG = 'est.';
const MODELLED_NOTE = 'Modelled estimate (GHS urban centre database), not a census count';

const STYLE_ID = 'wc-labels-style';
const STYLE = `
.wc-labels { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.wc-banner, .wc-country { position: absolute; left: 0; top: 0; will-change: transform; white-space: nowrap; }
.wc-banner { display: flex; align-items: stretch; box-sizing: border-box; font: 600 11px "Alegreya Sans SC", "Alegreya Sans", "Trebuchet MS", sans-serif;
  letter-spacing: 0.04em; color: #f4efe2; border-radius: 3px; overflow: hidden;
  background: linear-gradient(#1d3147, #122335); box-shadow: 0 1px 0 rgba(255,255,255,0.12) inset, 0 2px 5px rgba(0,0,0,0.45);
  border: 1px solid rgba(214, 180, 106, 0.85); }
.wc-banner .wc-plate { display: flex; align-items: center; justify-content: center; flex: none; letter-spacing: 0;
  box-shadow: inset -1px 0 0 rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.28); }
.wc-banner .wc-name { padding: 0 6px 0 5px; text-transform: uppercase; }
.wc-banner .wc-pop { padding: 0 5px; background: rgba(0,0,0,0.28); color: #d9e6ef; font-weight: 500; }
.wc-banner .wc-year { font-size: 8px; color: #9fb1c0; margin-left: 2px; }
.wc-banner .wc-tag { font-size: 8px; font-weight: 500; color: #9fb1c0; margin-left: 4px; }
.wc-banner .wc-marks { display: flex; align-items: center; gap: 3px; padding: 0 5px 0 4px; background: rgba(0,0,0,0.34); }
.wc-banner .wc-mark { display: block; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.6)); }
.wc-country { font-weight: 700; line-height: 1; font-family: "Cinzel", "Trajan Pro", "Cormorant SC", Georgia, serif;
  text-transform: uppercase; color: rgba(255, 250, 238, 0.9);
  text-shadow: 0 0 3px rgba(10, 18, 30, 0.95), 0 0 8px rgba(10, 18, 30, 0.6), 0 1px 1px rgba(0,0,0,0.9); }
`;

// Status marks, drawn in a 12 × 12 box. Occupied takes the occupier's colour, the others a fixed one.
const MARK_COLOR: Record<Exclude<Mark['kind'], 'occupied'>, string> = { war: '#ff8f78', nearFront: '#ffd166' };
const MARK_SVG: Record<Mark['kind'], string> = {
  war: '<path d="M2.2 2.2 9.8 9.8M9.8 2.2 2.2 9.8M1.4 4.4 4.4 1.4M10.6 4.4 7.6 1.4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  nearFront: '<path d="M2 10.5V4.5h1.6V3h1.3v1.5h2.2V3h1.3v1.5H10v6z" fill="currentColor" stroke="#1a2230" stroke-width="0.6" stroke-linejoin="round"/>',
  occupied: '<path d="M3 11V1.6" stroke="#f4efe2" stroke-width="1.3" stroke-linecap="round"/><path d="M3.6 2H10L8.6 4.4 10 6.8H3.6z" fill="currentColor" stroke="#0c1520" stroke-width="0.6" stroke-linejoin="round"/>',
};
// What each mark claims, as its tooltip: the near-front mark is a distance to a sourced front, not a siege.
const MARK_NOTE: Record<Mark['kind'], string> = {
  war: 'War: the country is a theatre of a major war',
  nearFront: 'Near the front: a sourced front line lies within about 1.5 hexes. Not a claim of siege',
  occupied: 'Occupied: held by another state, per the control record',
};
// Actor indices are Uint16 in the snapshot; the real ones stay far below this.
const MAX_ACTORS = 1 << 16;
const NEUTRAL_COLORS = { primary: '#6d7f91', secondary: '#d8dde2' };

/** What the snapshot may give beyond WorldSnapshot: who sources the control of a hex (contract request). */
interface SnapshotSources {
  controlSources?: StatusInput['controlSources'];
}

/** What a city record may say beyond City: the span it was its country's capital, [from, to) as dates (contract request). */
interface CapitalSpan {
  capital_from?: string;
  capital_to?: string;
}

/** The towns a record's urban centre merges, as the record names them (contract request). */
interface CentreNames {
  centre_names?: readonly string[];
}

/** A banner to draw: a snapshot city, or the capital of a city-state that has no city on its hex. */
interface Entry {
  /** City id; the negated actor index for a city-state capital (never clashes with a city id). */
  id: number;
  name: string;
  hex: number;
  /** 0 when the record gives none (city-state capitals): no population is drawn then. */
  pop: number;
  popYear: number;
  capital: boolean;
  /** Towns of the urban centre when the record names several: the population counts all of them. */
  centre: readonly string[];
  iso3: string;
  sources: SourceId[];
  /** Actor a city-state banner belongs to; 0 for a city. */
  actor: number;
  /** State the city belongs to (its own country, else the hex owner): the plate takes its colours. */
  home: number;
  /** Actor holding the city's land against its own state's ownership; 0 = nobody. */
  occupier: number;
}

interface Banner {
  entry: Entry;
  el: HTMLDivElement;
  w: number;
  size: BannerSize;
  marks: Mark[];
  /** What the element was built from: state, occupier, size, population and year, marks. */
  key: string;
  /** Text-measure generation the width was taken in. */
  metrics: number;
  /** Snapshot the marks were computed for. */
  epoch: number;
  shown: boolean;
}

/** A place for a name, as fractions of the territory's length from the anchor: along its axis and to its side. */
interface Spot {
  along: number;
  side: number;
}

/** A spot for a name resolved to the screen: its middle, the way the territory runs there, its best fit. */
interface ScreenSpot {
  /** Index in the anchor's spots: the key of its ground verdicts. */
  slot: number;
  x: number;
  y: number;
  angle: number;
  size: number;
  spacing: number;
}

/** Whether the ground under a name is its country's, and when that was read. */
interface Verdict {
  ok: boolean;
  view: number;
  travel: number;
  truth: number;
}

interface CountryAnchor {
  index: number;
  hex: number;
  /** Direction of the territory and its extent along it. */
  axis: Axis;
  /** Where the name may sit, all on the territory, the anchor first. */
  spots: Spot[];
  el: HTMLDivElement | null;
  shown: boolean;
  /** The spots on the screen, by slot, for the view and the truth they were projected in; undefined = not tried yet. */
  seen: { view: number; truth: number; at: Array<ScreenSpot | null | undefined> } | null;
  /** Verdicts by spot and size step (spot * SIZE_STEPS + step). */
  ground: Map<number, Verdict>;
}

interface FrontTag {
  front: FrontRecord;
  el: HTMLDivElement;
  w: number;
  shown: boolean;
}

export class LabelLayer {
  readonly root = document.createElement('div');
  private readonly camera: THREE.PerspectiveCamera;
  private readonly heightOf: (hex: number) => number;
  private readonly actors = new Map<number, ActorLabel>();
  private readonly actorIds = new Map<string, number>();
  private readonly actorWatchers = new Set<() => void>();
  private readonly widths = new Map<string, number>();
  private readonly canvas = document.createElement('canvas').getContext('2d');
  private banners = new Map<number, Banner>();
  private tags = new Map<string, FrontTag>();
  private obstacles: () => readonly ScreenRect[];
  private cities: City[] = [];
  private entries: Entry[] = [];
  private owner: Uint16Array | null = null;
  private date = '';
  /** 1 at the index of every actor that holds a hex, or is the default owner of one, on the snapshot's date. */
  private readonly holds = new Uint8Array(MAX_ACTORS);
  private status: StatusInput | null = null;
  private due: WorldSnapshot | null = null;
  /** The owner map whose country anchors are yet to be worked out (see applySnapshot). */
  private anchorsFor: Uint16Array | null = null;
  private appliedAt = 0;
  private epoch = 0;
  private metrics = 0;
  private anchors: CountryAnchor[] = [];
  private ownerKey = '';
  private last: LabelCounts = { banners: 0, countries: 0, marks: 0, unsourced: 0 };
  private readonly v = new THREE.Vector3();
  private readonly ray = new THREE.Vector3();
  private top: number | null = null;
  private readonly view = new Float64Array(VIEW_LEN);
  private readonly probe = new Float64Array(VIEW_LEN);
  /** Counts the views the camera moved to; every screen position worked out in an earlier one is stale. */
  private viewId = 0;
  /** How far the camera has travelled in all (TRAVEL_STANDS). */
  private travel = 0;
  /** Counts what a ground check reads besides the view: the owner map, the names and the text widths. */
  private truth = 0;
  private warmed: { anchors: CountryAnchor[]; truth: number } | null = null;

  constructor(container: HTMLElement, camera: THREE.PerspectiveCamera, heightOf: (hex: number) => number) {
    this.camera = camera;
    this.heightOf = heightOf;
    this.root.className = 'wc-labels';
    container.appendChild(this.root);
    this.obstacles = hudRects(this.root);
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = STYLE;
      document.head.appendChild(style);
    }

    // Text widths measured before the web fonts arrive are wrong: measure again once they are in.
    void document.fonts?.ready.then(() => {
      this.widths.clear();
      this.metrics++;
      this.truth++;
    });
  }

  /** Actors set by the last setActors. */
  actorList(): readonly ActorLabel[] {
    return [...this.actors.values()];
  }

  /** Call `cb` after every setActors; returns a function that stops it. */
  onActors(cb: () => void): () => void {
    this.actorWatchers.add(cb);
    return () => this.actorWatchers.delete(cb);
  }

  /** Actor index of an actor id (ActorLabel.id); 0 when unknown. */
  indexOf(id: string): number {
    return this.actorIds.get(id) ?? 0;
  }

  /**
   * Boxes (label-layer px) that names, tags and banners keep clear of: the HUD widgets over the map.
   * The default reads them off the page (hudRects.ts); a host that knows them better sets its own.
   */
  setObstacles(read: () => readonly ScreenRect[]): void {
    this.obstacles = read;
  }

  /** Names, colours and sources per actor index (countries.json and world actors). */
  setActors(list: readonly ActorLabel[]): void {
    this.actors.clear();
    this.actorIds.clear();
    for (const a of list) {
      this.actors.set(a.index, a);
      if (a.id !== undefined && !this.actorIds.has(a.id)) {
        this.actorIds.set(a.id, a.index);
      }
    }
    for (const b of this.banners.values()) {
      b.el.remove();
    }
    this.banners.clear();
    for (const a of this.anchors) {
      a.el?.remove();
      a.el = null;
    }
    this.truth++;
    this.entries = this.entriesOf(this.cities);
    for (const cb of this.actorWatchers) {
      cb();
    }
  }

  /** The snapshot is applied by the next update: a burst of them, as in a drag on the time slider, costs one. */
  setSnapshot(s: WorldSnapshot): void {
    this.due = s;
  }

  private applySnapshot(s: WorldSnapshot): void {
    const now = performance.now();
    const gap = now - this.appliedAt;
    this.appliedAt = now;
    this.cities = [...s.cities];
    this.owner = s.owner;
    this.date = s.date;
    this.truth++;
    this.markHolders(s.owner, s.claim);
    // The status first: the entries read each city's state and occupier off it.
    this.status = {
      date: s.date,
      owner: s.owner,
      controller: s.controller,
      control: s.control,
      controlFrac: s.controlFrac,
      wars: s.activeWars,
      fronts: frontRecords(s, (id) => this.indexOf(id)),
      actorOf: (iso3) => this.indexOf(iso3),
      controlSources: (s as SnapshotSources).controlSources,
    };
    this.entries = this.entriesOf(this.cities);
    this.setTags(this.status.fronts);
    // Banners follow the new date in place: their plate and marks are re-derived when next drawn.
    this.epoch++;

    const key = ownerSignature(s.owner);
    if (key === this.ownerKey) {
      return;
    }

    // Anchors take tens of ms to work out. While the date is dragged they stay those of the last owners,
    // which is safe (a name is only set where the ground under it is its country's, on the new owners),
    // and are worked out once the drag rests.
    this.ownerKey = key;
    this.anchorsFor = s.owner;
    if (this.anchors.length === 0 || gap >= SCRUB_MS) {
      this.buildAnchors();
    }
  }

  private buildAnchors(): void {
    const owner = this.anchorsFor;
    if (!owner) {
      return;
    }

    this.anchorsFor = null;
    for (const a of this.anchors) {
      a.el?.remove();
    }
    const capitals = new Map<number, number>();
    for (const a of this.actors.values()) {
      if (a.capitalHex !== undefined) {
        capitals.set(a.index, a.capitalHex);
      }
    }
    this.anchors = computeAnchors(owner, capitals);
  }

  counts(): LabelCounts {
    return this.last;
  }

  /**
   * Take in what is due without drawing: the snapshot, the country anchors and the width of every name.
   * The host calls it while the labels are hidden (the globe), so the first flat frame only projects.
   */
  prepare(): void {
    this.settle();
    if (this.warmed?.anchors === this.anchors && this.warmed.truth === this.truth) {
      return;
    }

    for (const a of this.anchors) {
      const actor = this.actors.get(a.index);
      if (actor) {
        this.countryEm(actor.name);
      }
    }
    this.warmed = { anchors: this.anchors, truth: this.truth };
  }

  /** Apply the due snapshot, and the anchors once the date stops being dragged. */
  private settle(): void {
    if (this.due) {
      const due = this.due;
      this.due = null;
      this.applySnapshot(due);
    }
    if (this.anchorsFor && performance.now() - this.appliedAt >= SCRUB_MS) {
      this.buildAnchors();
    }
  }

  /** Position and declutter everything for this frame. */
  update(camX: number, camHeight: number): void {
    this.settle();

    const w = this.root.clientWidth;
    const h = this.root.clientHeight;
    this.follow(camX, w, h);
    // The HUD's boxes are taken before anything is placed.
    const taken: Array<[number, number, number, number]> = this.obstacles().map((r) => [r[0], r[1], r[2], r[3]]);
    let unsourced = 0;

    // Capital banners first, then the country names (few and large) and the rest of the banners around
    // them: a name gives way to a capital, never the reverse.
    const seen = new Set<number>();
    const plan = this.bannerPlan(camX, camHeight, w, h);
    if (plan) {
      this.placeBanners(plan.capitals, plan.size, taken, seen);
    }

    let countries = 0;
    for (const a of this.anchors) {
      const shown = camHeight >= COUNTRY_MIN_HEIGHT && this.placeCountry(a, camX, w, h, taken);
      if (shown !== a.shown && a.el) {
        this.toggle(a.el, shown);
      }
      a.shown = shown;
      if (shown) {
        countries++;
        unsourced += (this.actors.get(a.index)?.sources.length ?? 0) > 0 ? 0 : 1;
      }
    }

    unsourced += this.placeTags(camX, camHeight, w, h, taken);

    if (plan) {
      this.placeBanners(plan.others, plan.size, taken, seen);
    }

    let marks = 0;
    for (const b of this.banners.values()) {
      const shown = seen.has(b.entry.id);
      if (shown !== b.shown) {
        this.toggle(b.el, shown);
        b.shown = shown;
      }
      if (shown) {
        marks += b.marks.length;
        unsourced += b.entry.sources.length > 0 ? 0 : 1;
      }
    }
    this.last = { banners: seen.size, countries, marks, unsourced };
  }

  dispose(): void {
    this.root.remove();
  }

  /** Count a new view when the camera or the viewport moved, and how far the camera has travelled. */
  private follow(camX: number, w: number, h: number): void {
    const m = this.camera.matrixWorld.elements;
    const p = this.camera.projectionMatrix.elements;
    const now = this.probe;
    for (let i = 0; i < VIEW_MATRIX; i++) {
      now[i] = m[i];
    }
    now[VIEW_MATRIX] = p[0];
    now[VIEW_MATRIX + 1] = p[5];
    now[VIEW_MATRIX + 2] = camX;
    now[VIEW_MATRIX + 3] = w;
    now[VIEW_MATRIX + 4] = h;

    let moved = false;
    for (let i = 0; i < VIEW_LEN && !moved; i++) {
      moved = Math.abs(now[i] - this.view[i]) > VIEW_EPS;
    }
    if (!moved) {
      return;
    }

    // Turn: the axes' nine numbers, about a radian each for a radian of turn. Move: in camera heights.
    // A projection or viewport change is a leap.
    const height = Math.max(Math.abs(m[13]), 1);
    let turn = 0;
    for (const i of ROTATION) {
      turn += Math.abs(now[i] - this.view[i]);
    }
    const move = (Math.abs(now[12] - this.view[12]) + Math.abs(now[13] - this.view[13]) + Math.abs(now[14] - this.view[14])) / height;
    const leap = Math.abs(now[VIEW_MATRIX] - this.view[VIEW_MATRIX]) + Math.abs(now[VIEW_MATRIX + 1] - this.view[VIEW_MATRIX + 1]) + Math.abs(now[VIEW_MATRIX + 3] - this.view[VIEW_MATRIX + 3]) + Math.abs(now[VIEW_MATRIX + 4] - this.view[VIEW_MATRIX + 4]) > VIEW_EPS;
    this.travel += turn + move + (leap ? LEAP_TRAVEL : 0);
    this.view.set(now);
    this.viewId++;
  }

  /** The banners to try at this camera height, capitals apart from the rest; null when none show this high. */
  private bannerPlan(camX: number, camHeight: number, w: number, h: number): { size: BannerSize; capitals: Candidate[]; others: Candidate[] } | null {
    const band = CITY_BANDS.find((b) => camHeight < b.below);
    if (!band) {
      return null;
    }

    const cand = this.candidates(band, camX, w, h);
    return { size: camHeight >= SMALL_FROM ? 'small' : 'full', capitals: cand.filter((c) => c.capital), others: cand.filter((c) => !c.capital) };
  }

  /** Place `tier` in order; banners already on screen keep their place before newcomers try, so a banner does not flicker as the camera drifts. */
  private placeBanners(tier: readonly Candidate[], size: BannerSize, taken: Array<[number, number, number, number]>, seen: Set<number>): void {
    for (const stay of ['kept', 'fresh'] as const) {
      for (const c of tier) {
        if (seen.size >= MAX_BANNERS) {
          return;
        }
        if (seen.has(c.entry.id) || (this.banners.get(c.entry.id)?.shown === true) !== (stay === 'kept')) {
          continue;
        }
        if (this.placeBanner(c.entry, size, c.x, c.y, taken)) {
          seen.add(c.entry.id);
        }
      }
    }
  }

  /** Fade an element in; out is immediate (banner rects are read by other layers as display). */
  private toggle(el: HTMLElement, shown: boolean): void {
    el.style.display = shown ? '' : 'none';
    if (shown) {
      el.animate?.([{ opacity: 0 }, { opacity: 1 }], FADE_MS);
    }
  }

  /** Cities and city-state capitals to try this frame, in priority order, with their screen spot. */
  private candidates(band: (typeof CITY_BANDS)[number], camX: number, w: number, h: number): Candidate[] {
    const out: Candidate[] = [];
    for (const entry of this.entries) {
      if (entry.pop < band.minPop && !(band.capitals && entry.capital)) {
        continue;
      }

      const p = this.project(entry.hex, camX, w, h);
      if (p) {
        out.push({ entry, capital: entry.capital, x: p.x, y: p.y });
      }
    }
    return out;
  }

  /** Try to put the banner at (x, y); false when it would overlap one already placed. */
  private placeBanner(entry: Entry, size: BannerSize, x: number, y: number, taken: Array<[number, number, number, number]>): boolean {
    const b = this.banner(entry, size);
    const height = METRICS[size].height;
    const x0 = x - b.w / 2;
    const y0 = y - BANNER_LIFT_PX - height;
    const rect: [number, number, number, number] = [x0 - LABEL_PAD_PX, y0 - LABEL_PAD_PX, x0 + b.w + LABEL_PAD_PX, y0 + height + LABEL_PAD_PX];
    if (overlaps(taken, rect)) {
      return false;
    }

    taken.push(rect);
    b.el.style.transform = `translate3d(${x0.toFixed(1)}px, ${y0.toFixed(1)}px, 0)`;
    return true;
  }

  /** Tags for the fronts older than a turn; one per front, rebuilt when its date changes. */
  private setTags(fronts: readonly FrontRecord[]): void {
    const keep = new Map<string, FrontTag>();
    for (const front of fronts) {
      if (front.age <= FRESH_DAYS) {
        continue;
      }

      const old = this.tags.get(front.id);
      if (old && old.front.asOf === front.asOf) {
        old.front = front;
        keep.set(front.id, old);
        continue;
      }

      old?.el.remove();
      keep.set(front.id, this.buildTag(front));
    }

    for (const [id, tag] of this.tags) {
      if (keep.get(id) !== tag) {
        tag.el.remove();
      }
    }
    this.tags = keep;
  }

  private buildTag(front: FrontRecord): FrontTag {
    const m = METRICS.full;
    const text = `Front as of ${front.asOf}`;
    const el = document.createElement('div');
    el.className = 'wc-banner';
    el.style.height = `${m.height}px`;
    el.style.fontSize = `${m.font}px`;
    el.style.lineHeight = `${m.height - BORDER_PX * 2}px`;
    el.dataset.sources = front.sources.join(' ');
    el.dataset.frontAsOf = front.asOf;

    const name = document.createElement('span');
    name.className = 'wc-name';
    name.textContent = text;
    el.appendChild(name);
    el.style.display = 'none';
    this.root.appendChild(el);

    const width = BORDER_PX * 2 + NAME_PAD_PX + this.measure(text.toUpperCase(), `600 ${m.font}px ${BANNER_FAMILY}`, TRACKING_EM * m.font);
    return { front, el, w: Math.ceil(width), shown: false };
  }

  /** Put each tag on its front's middle when there is room; returns how many drawn lack a source. */
  private placeTags(camX: number, camHeight: number, w: number, h: number, taken: Array<[number, number, number, number]>): number {
    let unsourced = 0;

    for (const tag of this.tags.values()) {
      const p = camHeight < FRONT_TAG_BELOW ? this.projectWorld(tag.front.mid.x, this.heightAtWorld(tag.front.mid), tag.front.mid.z, camX, w, h, 'visible') : null;
      const height = METRICS.full.height;
      const rect: [number, number, number, number] | null = p ? [p.x - tag.w / 2 - LABEL_PAD_PX, p.y - height / 2 - LABEL_PAD_PX, p.x + tag.w / 2 + LABEL_PAD_PX, p.y + height / 2 + LABEL_PAD_PX] : null;
      const shown = p !== null && rect !== null && !overlaps(taken, rect);
      if (shown !== tag.shown) {
        this.toggle(tag.el, shown);
        tag.shown = shown;
      }
      if (!shown || !p || !rect) {
        continue;
      }

      taken.push(rect);
      tag.el.style.transform = `translate3d(${(p.x - tag.w / 2).toFixed(1)}px, ${(p.y - height / 2).toFixed(1)}px, 0)`;
      unsourced += tag.front.sources.length > 0 ? 0 : 1;
    }
    return unsourced;
  }

  private heightAtWorld(at: { x: number; z: number }): number {
    return this.heightOf(hexAtWorld(at.x, at.z)) + LABEL_Y;
  }

  /**
   * Marks in `holds` the actors that own land in `owner`, or that the default worldview gives land
   * the view gives to another (`claim`): Taiwan exists in Xi's view, only as a claim of another state.
   */
  private markHolders(owner: Uint16Array, claim?: Uint16Array): void {
    this.holds.fill(0);
    for (let i = 0; i < owner.length; i++) {
      this.holds[owner[i]] = 1;
    }
    for (let i = 0; claim && i < claim.length; i++) {
      this.holds[claim[i]] = 1;
    }
  }

  /**
   * Ids of the cities that are their country's capital on the snapshot's date. The record's flag is
   * static, so it is read against the date: inside the span the record gives, if any; while the
   * state holds land (a city-state needs none); and, where a country flags several cities, only the
   * one standing on the country's capital hex (Nay Pyi Taw, not Yangon).
   */
  private capitalIds(cities: readonly City[]): Set<number> {
    const flagged = new Map<string, City[]>();
    for (const c of cities) {
      if (!c.capital || !this.inTerm(c) || !this.stateStands(c.iso3)) {
        continue;
      }

      const list = flagged.get(c.iso3) ?? [];
      list.push(c);
      flagged.set(c.iso3, list);
    }

    const out = new Set<number>();
    for (const [iso3, list] of flagged) {
      const seat = this.actors.get(this.indexOf(iso3))?.capitalHex;
      const onSeat = list.filter((c) => c.hex === seat);
      for (const c of onSeat.length > 0 ? onSeat : list) {
        out.add(c.id);
      }
    }

    return out;
  }

  /** Whether the date lies in the span the record gives for the capital; a record with no span always does. */
  private inTerm(c: City): boolean {
    const { capital_from: from, capital_to: to } = c as City & CapitalSpan;
    return (from === undefined || from <= this.date) && (to === undefined || this.date < to);
  }

  /** Whether the country exists on the date: it holds land (or the default worldview gives it some), is a city-state, or is not known to the layer. */
  private stateStands(iso3: string): boolean {
    const index = this.indexOf(iso3);
    if (index === 0 || this.actors.get(index)?.cityState || !this.owner) {
      return true;
    }

    return this.holds[index] === 1;
  }

  private entriesOf(cities: readonly City[]): Entry[] {
    const capitals = this.capitalIds(cities);
    const out: Entry[] = cities.map((c) => {
      // Its own country's colours and occupation, even where the hex is a neighbour's or open water.
      const site = this.status ? citySite(c.hex, c.iso3, this.status) : { home: this.indexOf(c.iso3), hex: c.hex, occupier: 0 };
      return {
        id: c.id,
        name: c.name,
        hex: c.hex,
        pop: c.pop,
        popYear: c.pop_year,
        capital: capitals.has(c.id),
        centre: (c as City & CentreNames).centre_names ?? [],
        iso3: c.iso3,
        sources: c.sources,
        actor: 0,
        home: site.home,
        occupier: site.occupier,
      };
    });

    // A city-state with no city on its capital hex (Monaco's ring hex holds no Monaco city in some
    // years) still gets its banner: the state's name, its sources, no population.
    const taken = new Set(cities.map((c) => c.hex));
    for (const a of this.actors.values()) {
      if (!a.cityState || a.capitalHex === undefined || taken.has(a.capitalHex)) {
        continue;
      }

      taken.add(a.capitalHex);
      out.push({ id: -a.index, name: a.name, hex: a.capitalHex, pop: 0, popYear: 0, capital: true, centre: [], iso3: a.id ?? '', sources: a.sources, actor: a.index, home: a.index, occupier: 0 });
    }

    return out.sort(priority);
  }

  /**
   * Set the name at the largest size that fits: at the anchor, else at the spots along the territory's
   * axis, and on to smaller sizes down to the smallest legible one. The name is shown whole or not,
   * and only where the ground under it (middle and both ends) is its own country's or nobody's.
   */
  private placeCountry(a: CountryAnchor, camX: number, w: number, h: number, taken: Array<[number, number, number, number]>): boolean {
    const actor = this.actors.get(a.index);
    if (!actor) {
      return false;
    }

    const em = this.countryEm(actor.name);
    const seen = this.seenOn(a);

    for (const [stepAt, step] of SIZE_STEPS.entries()) {
      for (let slot = 0; slot < a.spots.length; slot++) {
        const spot = this.spotAt(a, seen, slot, camX, em, actor.name.length, w, h);
        if (!spot) {
          continue;
        }

        const size = Math.max(SIZE_FLOOR_PX, spot.size * step);
        const width = size * (em + actor.name.length * spot.spacing);
        const { hw, hh } = turnedHalf(width, size, spot.angle);
        const rect: [number, number, number, number] = [spot.x - hw, spot.y - hh, spot.x + hw, spot.y + hh];
        // A country name cut by the screen edge reads as a different word ("ISTAN"): show it whole or not.
        if (rect[0] < 0 || rect[1] < 0 || rect[2] > w || rect[3] > h || overlaps(taken, rect)) {
          continue;
        }
        if (!this.groundOk(a, spot, stepAt, width * HALF, w, h)) {
          continue;
        }

        const el = a.el ?? this.countryEl(a, actor);
        el.style.fontSize = `${size.toFixed(1)}px`;
        el.style.letterSpacing = `${spot.spacing.toFixed(3)}em`;
        el.style.paddingLeft = `${spot.spacing.toFixed(3)}em`;
        taken.push(rect);
        el.style.transform = `translate3d(${spot.x.toFixed(1)}px, ${spot.y.toFixed(1)}px, 0) translate(-50%, -50%) rotate(${spot.angle.toFixed(4)}rad)`;
        return true;
      }
    }
    return false;
  }

  /** The name's spot cache: emptied when the view or what the spots read moved. */
  private seenOn(a: CountryAnchor): NonNullable<CountryAnchor['seen']> {
    if (a.seen?.view === this.viewId && a.seen.truth === this.truth) {
      return a.seen;
    }

    a.seen = { view: this.viewId, truth: this.truth, at: new Array(a.spots.length) };
    return a.seen;
  }

  /**
   * One spot of a name on the screen, worked out when first tried in a view (the anchor's own spot
   * usually holds, and the others are never projected). Every spot is projected from its own place on
   * the ground. Worked out on screen from the anchor's projection, a spot drifts onto other countries
   * wherever the camera is tilted (the anchor of a country behind the camera projects far off screen,
   * and steps taken from there land anywhere).
   */
  private spotAt(a: CountryAnchor, seen: NonNullable<CountryAnchor['seen']>, slot: number, camX: number, em: number, letters: number, w: number, h: number): ScreenSpot | null {
    const hit = seen.at[slot];
    if (hit !== undefined) {
      return hit;
    }

    const c = hexCenter(hexCol(a.hex), hexRow(a.hex));
    const ux = camX + wrapDelta(camX, c.x);
    const spot = this.screenSpot(a, a.spots[slot], slot, ux, c.z, em, letters, w, h);
    seen.at[slot] = spot;
    return spot;
  }

  /**
   * Whether the ground under a name at a size is its country's (onOwnGround), kept while nothing it reads
   * moved. A camera that moves leaves a verdict standing for a stretch of travel that differs from name to
   * name, so the marches of a whole map are not all redone on one frame. A new snapshot, other names or
   * widths, and a leap of the camera, stand for nothing: the first check of a name is always made.
   */
  private groundOk(a: CountryAnchor, spot: ScreenSpot, step: number, half: number, w: number, h: number): boolean {
    const key = spot.slot * SIZE_STEPS.length + step;
    const hit = a.ground.get(key);
    if (hit?.truth === this.truth) {
      const share = 1 + (a.index % TRAVEL_SPREAD) / TRAVEL_SPREAD;
      if (hit.view === this.viewId || this.travel - hit.travel < TRAVEL_STANDS * share) {
        return hit.ok;
      }
    }

    const ok = this.onOwnGround(a.index, spot, half, w, h);
    a.ground.set(key, { ok, view: this.viewId, travel: this.travel, truth: this.truth });
    return ok;
  }

  /**
   * A spot on the screen: null when its middle is off screen or behind the camera, or when the stretch
   * of the axis it has to itself cannot be measured (an end behind the camera).
   */
  private screenSpot(a: CountryAnchor, s: Spot, slot: number, ux: number, cz: number, em: number, letters: number, w: number, h: number): ScreenSpot | null {
    const x = ux + (a.axis.x * s.along - a.axis.z * s.side) * a.axis.length;
    const z = cz + (a.axis.z * s.along + a.axis.x * s.side) * a.axis.length;
    const y = this.heightAtWorld({ x, z });
    const p = this.projectAt(x, y, z, w, h, 'visible');
    if (!p) {
      return null;
    }

    // The run of the axis the spot has to itself: each spot has what is left on its side of the anchor.
    const room = Math.max(SPOT_SPAN_MIN, 1 - SPOT_SPAN_LOSS * Math.abs(s.along));
    const reach = a.axis.length * room * HALF;
    const end = this.projectAt(x + a.axis.x * reach, y, z + a.axis.z * reach, w, h, 'any');
    const start = this.projectAt(x - a.axis.x * reach, y, z - a.axis.z * reach, w, h, 'any');
    if (!end || !start) {
      return null;
    }

    const fit = fitLabel(Math.hypot(end.x - start.x, end.y - start.y), letters, em);
    if (!fit) {
      return null;
    }

    // Text never runs upside down: turn it half a turn when it would.
    let angle = Math.atan2(end.y - start.y, end.x - start.x);
    if (Math.abs(angle) > RIGHT_ANGLE) {
      angle += angle > 0 ? -Math.PI : Math.PI;
    }
    return { slot, x: p.x, y: p.y, angle, size: fit.size, spacing: fit.spacing };
  }

  /**
   * True when the ground under a name's middle is its country's, and along its whole length at least
   * OWN_SHARE_MIN of it is, the rest nobody's (a coast, a gap): never another country's, so a name
   * does not run over a neighbour or out over the sea between two lands.
   */
  private onOwnGround(index: number, spot: ScreenSpot, half: number, w: number, h: number): boolean {
    if (this.ownerUnder(spot.x, spot.y, w, h) !== index) {
      return false;
    }

    const dx = Math.cos(spot.angle) * half;
    const dy = Math.sin(spot.angle) * half;
    let misses = 0;
    for (const i of GROUND_ORDER) {
      const t = (i / (GROUND_SAMPLES - 1)) * 2 - 1;
      const held = this.ownerUnder(spot.x + t * dx, spot.y + t * dy, w, h);
      if (held === index) {
        continue;
      }
      if (held !== UNOWNED || ++misses > GROUND_MISSES_MAX) {
        return false;
      }
    }
    return true;
  }

  /** Owner of the ground under a screen pixel; -1 for the sky, or when no snapshot is set. */
  private ownerUnder(x: number, y: number, w: number, h: number): number {
    const hex = this.hexUnder(x, y, w, h);
    return hex < 0 || !this.owner ? -1 : this.owner[hex];
  }

  /**
   * The hex under a screen pixel: the first one its ray dips under. Met with the flat ground and refined,
   * the pick lands behind a ridge that hides it (a name over Eritrea reads as over the highlands in front).
   */
  private hexUnder(x: number, y: number, w: number, h: number): number {
    const cam = this.camera;
    this.ray.setFromMatrixPosition(cam.matrixWorld);
    this.v.set((x / w) * 2 - 1, -(y / h) * 2 + 1, HALF).unproject(cam).sub(this.ray).normalize();
    if (this.v.y >= 0) {
      return -1;
    }

    // Only the stretch of the ray between the tallest ground and the sea can meet the relief.
    const reach = -this.ray.y / this.v.y;
    const from = Math.min(reach, Math.max(0, (this.groundTop() - this.ray.y) / this.v.y));
    const step = Math.max(GROUND_MARCH_STEP, (reach - from) / GROUND_MARCH_MAX);
    for (let t = from; t < reach; t += step) {
      const hex = hexAtWorld(this.ray.x + this.v.x * t, this.ray.z + this.v.z * t);
      if (this.ray.y + this.v.y * t <= this.heightOf(hex)) {
        return hex;
      }
    }
    return hexAtWorld(this.ray.x + this.v.x * reach, this.ray.z + this.v.z * reach);
  }

  /** Height of the tallest ground, read once from the relief. */
  private groundTop(): number {
    if (this.top === null) {
      let top = 0;
      for (let hex = 0; hex < HEX_COUNT; hex++) {
        top = Math.max(top, this.heightOf(hex));
      }
      this.top = top;
    }
    return this.top;
  }

  private countryEl(a: CountryAnchor, actor: ActorLabel): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'wc-country';
    el.textContent = actor.name;
    el.dataset.sources = actor.sources.join(' ');
    el.style.display = 'none';
    this.root.appendChild(el);
    a.el = el;
    a.shown = false;
    return el;
  }

  /** Width of a name in em, letters unspaced, in the country face. */
  private countryEm(name: string): number {
    return this.measure(name.toUpperCase(), `700 ${MEASURE_EM}px ${COUNTRY_FAMILY}`, 0) / MEASURE_EM;
  }

  /** Text width in px in a css `font`, with `tracking` px added after every letter. */
  private measure(text: string, font: string, tracking: number): number {
    const key = `${font}|${tracking}|${text}`;
    const hit = this.widths.get(key);
    if (hit !== undefined) {
      return hit;
    }
    if (!this.canvas) {
      return text.length * tracking;
    }

    this.canvas.font = font;
    const width = this.canvas.measureText(text).width + text.length * tracking;
    this.widths.set(key, width);
    return width;
  }

  /** The banner of an entry, rebuilt only when what it shows (state, size, population, marks) changed. */
  private banner(entry: Entry, size: BannerSize): Banner {
    const hit = this.banners.get(entry.id);
    if (hit && hit.epoch === this.epoch && hit.size === size && hit.metrics === this.metrics) {
      return hit;
    }

    // Marks are read once per snapshot; a size change alone reuses them.
    const holder = entry.actor > 0 ? entry.actor : entry.home;
    const fresh = !hit || hit.epoch !== this.epoch;
    const marks = fresh ? this.marksOf(entry) : hit.marks;
    // Population and year are part of the key: the same city read at another date shows its own.
    const key = [holder, entry.occupier, size, entry.capital, entry.pop, entry.popYear, entry.sources.join(' '), marks.map((k) => k.kind).join('+')].join('|');
    if (hit && hit.key === key && hit.metrics === this.metrics) {
      hit.epoch = this.epoch;
      hit.entry = entry;
      return hit;
    }

    // A size change builds a new element: sites/icons.ts caches the size of each one.
    hit?.el.remove();
    const b = this.build(entry, size, holder, entry.occupier, marks, key);
    this.banners.set(entry.id, b);
    return b;
  }

  private marksOf(entry: Entry): Mark[] {
    return this.status ? cityMarks(entry.hex, entry.iso3, this.status) : [];
  }

  private build(entry: Entry, size: BannerSize, holder: number, occupier: number, marks: Mark[], key: string): Banner {
    const m = METRICS[size];
    const colors = this.actors.get(holder)?.colors ?? NEUTRAL_COLORS;
    const el = document.createElement('div');
    el.className = 'wc-banner';
    el.style.height = `${m.height}px`;
    el.style.fontSize = `${m.font}px`;
    el.style.lineHeight = `${m.height - BORDER_PX * 2}px`;
    el.dataset.sources = entry.sources.join(' ');

    const plate = document.createElement('span');
    plate.className = 'wc-plate';
    plate.style.width = `${m.plate}px`;
    plate.style.background = `linear-gradient(${colors.primary}, ${colors.primary} 70%, ${colors.secondary})`;
    plate.style.color = inkOn(colors.primary);
    plate.textContent = entry.capital ? '★' : '';
    const name = document.createElement('span');
    name.className = 'wc-name';
    name.textContent = entry.name;
    el.append(plate, name);

    const tracking = TRACKING_EM * m.font;
    let w = BORDER_PX * 2 + m.plate + NAME_PAD_PX + this.measure(entry.name.toUpperCase(), `600 ${m.font}px ${BANNER_FAMILY}`, tracking);
    if (size === 'full' && entry.centre.length >= CENTRE_MIN_TOWNS) {
      const tag = document.createElement('span');
      tag.className = 'wc-tag';
      tag.textContent = CENTRE_TAG;
      tag.title = `${CENTRE_NOTE} ${entry.centre.join('; ')}`;
      name.appendChild(tag);
      w += TAG_GAP_PX + this.measure(CENTRE_TAG, `500 ${TAG_PX}px ${BANNER_FAMILY}`, tracking);
    }

    if (size === 'full' && entry.pop > 0) {
      const text = formatPop(entry.pop);
      const pop = document.createElement('span');
      pop.className = 'wc-pop';
      pop.textContent = text;
      w += POP_PAD_PX + this.measure(text, `500 ${m.font}px ${BANNER_FAMILY}`, tracking);
      if (entry.popYear > 0) {
        const modelled = entry.popYear >= MODELLED_FROM;
        const year = document.createElement('span');
        year.className = 'wc-year';
        year.textContent = modelled ? `${entry.popYear} ${MODELLED_TAG}` : String(entry.popYear);
        pop.appendChild(year);
        if (modelled) {
          pop.title = MODELLED_NOTE;
        }
        w += YEAR_GAP_PX + this.measure(year.textContent, `500 ${YEAR_PX}px ${BANNER_FAMILY}`, tracking);
      }
      el.appendChild(pop);
    }

    if (marks.length > 0) {
      el.appendChild(this.marksEl(marks, m, occupier));
      el.dataset.marks = marks.map((k) => k.kind).join(' ');
      el.dataset.markSources = [...new Set(marks.flatMap((k) => k.sources))].join(' ');
      w += MARKS_PAD_PX + marks.length * m.mark + (marks.length - 1) * MARK_GAP_PX;
    }

    el.style.display = 'none';
    this.root.appendChild(el);
    return { entry, el, w: Math.ceil(w), size, marks, key, metrics: this.metrics, epoch: this.epoch, shown: false };
  }

  private marksEl(marks: readonly Mark[], m: Metrics, occupier: number): HTMLSpanElement {
    const box = document.createElement('span');
    box.className = 'wc-marks';
    for (const mark of marks) {
      const icon = document.createElement('span');
      icon.className = 'wc-mark';
      // Each icon cites the records behind its own claim, not the whole banner's.
      icon.dataset.kind = mark.kind;
      icon.dataset.sources = mark.sources.join(' ');
      icon.title = MARK_NOTE[mark.kind];
      icon.style.width = `${m.mark}px`;
      icon.style.height = `${m.mark}px`;
      icon.style.color = mark.kind === 'occupied' ? (this.actors.get(occupier)?.colors.primary ?? NEUTRAL_COLORS.primary) : MARK_COLOR[mark.kind];
      icon.innerHTML = `<svg viewBox="0 0 12 12" width="${m.mark}" height="${m.mark}">${MARK_SVG[mark.kind]}</svg>`;
      box.appendChild(icon);
    }
    return box;
  }

  private project(hex: number, camX: number, w: number, h: number): { x: number; y: number } | null {
    if (hex < 0 || hex >= HEX_COUNT) {
      return null;
    }
    const c = hexCenter(hexCol(hex), hexRow(hex));
    return this.projectWorld(c.x, this.heightOf(hex) + LABEL_Y, c.z, camX, w, h, 'visible');
  }

  private projectWorld(x: number, y: number, z: number, camX: number, w: number, h: number, mode: 'visible' | 'any'): { x: number; y: number } | null {
    return this.projectAt(camX + wrapDelta(camX, x), y, z, w, h, mode);
  }

  /** Project a point whose x is already the copy of the world to draw (no wrapping). */
  private projectAt(x: number, y: number, z: number, w: number, h: number, mode: 'visible' | 'any'): { x: number; y: number } | null {
    // In front of the camera first: a point behind it, or beside its lens, projects to a mirrored or
    // wildly stretched screen position that any test on the projected z can let through.
    this.v.set(x, y, z).applyMatrix4(this.camera.matrixWorldInverse);
    if (this.v.z > -this.camera.near) {
      return null;
    }

    this.v.applyMatrix4(this.camera.projectionMatrix);
    if (this.v.z < -1 || this.v.z > 1) {
      return null;
    }
    if (mode === 'visible' && (Math.abs(this.v.x) > 1 || Math.abs(this.v.y) > 1)) {
      return null;
    }
    return { x: (this.v.x * 0.5 + 0.5) * w, y: (-this.v.y * 0.5 + 0.5) * h };
  }
}

interface Candidate {
  entry: Entry;
  capital: boolean;
  x: number;
  y: number;
}

/** Capitals first, then by population. */
function priority(a: Entry, b: Entry): number {
  if (a.capital !== b.capital) {
    return a.capital ? -1 : 1;
  }
  return b.pop - a.pop;
}

function formatPop(pop: number): string {
  if (pop >= 1e6) {
    return `${(pop / 1e6).toFixed(pop >= 1e7 ? 0 : 1)}M`;
  }
  if (pop >= 1e3) {
    return `${Math.round(pop / 1e3)}k`;
  }
  return String(Math.round(pop));
}

function overlaps(list: ReadonlyArray<readonly [number, number, number, number]>, r: readonly [number, number, number, number]): boolean {
  for (const o of list) {
    if (r[0] < o[2] && r[2] > o[0] && r[1] < o[3] && r[3] > o[1]) {
      return true;
    }
  }
  return false;
}

/** Cheap change detector for the owner array (sampled checksum). */
function ownerSignature(owner: Uint16Array): string {
  let h = 2166136261;
  for (let i = 0; i < owner.length; i += 7) {
    h = Math.imul(h ^ owner[i], 16777619);
  }
  return `${owner.length}:${h >>> 0}`;
}

/** Mean position of `hexes` (x continuous across the date line by `ux`). */
function centroid(hexes: readonly number[], ux: ArrayLike<number>): { x: number; z: number } {
  let x = 0;
  let z = 0;
  for (const h of hexes) {
    x += ux[h];
    z += hexCenter(hexCol(h), hexRow(h)).z;
  }
  return { x: x / hexes.length, z: z / hexes.length };
}

/** The spots (SPOT_OFFSETS along, SIDE_OFFSETS aside) around `anchor` that are still on `index`'s land. */
function spotsOf(anchor: number, axis: Axis, owner: Uint16Array, index: number): Spot[] {
  const c = hexCenter(hexCol(anchor), hexRow(anchor));
  const spots: Spot[] = [];

  // Anchor row first: a name is set along the territory before it is set off to a side.
  for (const side of SIDE_OFFSETS) {
    for (const along of SPOT_OFFSETS) {
      const x = c.x + (axis.x * along - axis.z * side) * axis.length;
      const z = c.z + (axis.z * along + axis.x * side) * axis.length;
      if ((along === 0 && side === 0) || owner[hexAtWorld(x, z)] === index) {
        spots.push({ along, side });
      }
    }
  }

  return spots;
}

/**
 * One anchor per owner: the territory holding its capital (else its largest), and in it the hex
 * farthest from the territory's edge (a label there sits inside the country, not on a border).
 */
function computeAnchors(owner: Uint16Array, capitals: ReadonlyMap<number, number>): CountryAnchor[] {
  const comp = new Int32Array(HEX_COUNT).fill(-1);
  const best = new Map<number, { hexes: number[]; minX: number; maxX: number; id: number }>();
  const queue = new Int32Array(HEX_COUNT);
  const ux = new Float64Array(HEX_COUNT);
  let id = 0;
  for (let start = 0; start < HEX_COUNT; start++) {
    const o = owner[start];
    if (o === 0 || comp[start] >= 0) {
      continue;
    }
    // Flood the territory, tracking unwrapped x so Russia or Fiji across the date line measure right.
    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    comp[start] = id;
    ux[start] = hexCenter(hexCol(start), hexRow(start)).x;
    const hexes: number[] = [];
    let minX = Infinity;
    let maxX = -Infinity;
    while (head < tail) {
      const t = queue[head++];
      hexes.push(t);
      minX = Math.min(minX, ux[t]);
      maxX = Math.max(maxX, ux[t]);
      for (let d = 0; d < 6; d++) {
        const n = neighbour(t, d);
        if (n < 0 || comp[n] >= 0 || owner[n] !== o) {
          continue;
        }
        comp[n] = id;
        ux[n] = ux[t] + wrapDelta(ux[t], hexCenter(hexCol(n), hexRow(n)).x);
        queue[tail++] = n;
      }
    }
    const prev = best.get(o);
    const capital = capitals.get(o);
    const holdsCapital = capital !== undefined && comp[capital] === id;
    const prevHolds = prev !== undefined && capital !== undefined && comp[capital] === prev.id;
    if (!prev || holdsCapital || (!prevHolds && hexes.length > prev.hexes.length)) {
      best.set(o, { hexes, minX, maxX, id });
    }
    id++;
  }

  const dist = new Int32Array(HEX_COUNT).fill(-1);
  const anchors: CountryAnchor[] = [];
  for (const [index, t] of best) {
    // Distance from the territory's edge, by breadth-first rings.
    let head = 0;
    let tail = 0;
    for (const h of t.hexes) {
      let edge = false;
      for (let d = 0; d < 6 && !edge; d++) {
        const n = neighbour(h, d);
        edge = n < 0 || owner[n] !== index;
      }
      if (edge) {
        dist[h] = 0;
        queue[tail++] = h;
      }
    }
    // Among the deepest hexes, the one nearest the territory's middle: a wide plateau (a rectangle
    // country) would otherwise put its name at whichever end the flood found first.
    const mid = centroid(t.hexes, ux);
    const off = (hex: number): number => Math.hypot(ux[hex] - mid.x, hexCenter(hexCol(hex), hexRow(hex)).z - mid.z);
    let far = t.hexes[0];
    while (head < tail) {
      const h = queue[head++];
      if (dist[h] > dist[far] || (dist[h] === dist[far] && off(h) < off(far))) {
        far = h;
      }
      for (let d = 0; d < 6; d++) {
        const n = neighbour(h, d);
        if (n < 0 || owner[n] !== index || dist[n] >= 0) {
          continue;
        }
        dist[n] = dist[h] + 1;
        queue[tail++] = n;
      }
    }
    const axis = principalAxis(t.hexes, ux);
    anchors.push({ index, hex: far, axis, spots: spotsOf(far, axis, owner, index), el: null, shown: false, seen: null, ground: new Map() });
  }
  // Big countries first, so their names win the declutter.
  return anchors.sort((a, b) => b.axis.length - a.axis.length);
}
